import React, { useState, useEffect } from "react";
import { useLanguage } from "@/hooks/useLanguage";

// Cookie utility functions
const setCookie = (name: string, value: string, days: number) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/`;
};

const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length);
    }
  }
  return null;
};

const WelcomeModal: React.FC = () => {
  const { currentLanguage, switchLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [displayLanguage, setDisplayLanguage] = useState<"ar" | "en">(
    currentLanguage
  );

  useEffect(() => {
    // Check if user has seen the modal before
    const hasSeenModal = getCookie("welcomeModalShown");
    if (!hasSeenModal) {
      setIsOpen(true);
    }
  }, []);

  useEffect(() => {
    setDisplayLanguage(currentLanguage);
  }, [currentLanguage]);

  const handleClose = () => {
    // Set cookie to remember that user has seen the modal (expires in 1 year)
    setCookie("welcomeModalShown", "true", 365);
    setIsOpen(false);
  };

  const handleLanguageSwitch = () => {
    const newLanguage = displayLanguage === "ar" ? "en" : "ar";
    setDisplayLanguage(newLanguage);
    switchLanguage(newLanguage);
  };

  if (!isOpen) return null;

  // Determine the welcome image based on language
  const welcomeImage = `/images/welcome-images/Welcome-${displayLanguage}.jpg`;

  // Button texts
  const closeButtonText = displayLanguage === "ar" ? "إغلاق" : "Close";
  const languageSwitchText = displayLanguage === "ar" ? "English" : "العربية";

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div
        className="bg-black/20 backdrop-blur-md rounded-xl overflow-hidden border border-white/10 flex flex-col items-center max-w-5xl w-full relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Language Switch Button */}
        <button
          onClick={handleLanguageSwitch}
          className="absolute top-4 right-4 rtl:right-auto rtl:left-4 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-blue-500/30 text-white/90 rounded-lg backdrop-blur-sm transition-all duration-200 text-sm font-medium border border-white/20"
        >
          {languageSwitchText}
        </button>

        <div className="p-4 w-full flex flex-col items-center">
          <img
            src={welcomeImage}
            alt="Welcome"
            className="object-contain max-h-[80vh] rounded-xl shadow-2xl"
          />
          <button
            onClick={handleClose}
            className="mt-6 px-10 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white/90 rounded-xl hover:shadow-lg hover:shadow-blue-500/30 shadow-blue-500/20 transition-all duration-200 shadow-md font-bold"
          >
            {closeButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;
